import { t } from "@/lib/i18n/interpolate";

export type EssayType =
  | "argumentative"
  | "analytical"
  | "expository"
  | "compare";

/**
 * Title frames. Each one reads correctly with a singular or a plural topic ("school
 * uniforms", "remote work"), so none puts a verb or a pronoun after the topic. `{topic}`
 * marks where the writer's topic goes; a translated content file replaces the frames
 * through `ui`.
 */
export const TITLE_FRAMES: Record<EssayType, readonly string[]> = {
  argumentative: [
    "The case for {topic}: why this matters",
    "Giving {topic} a second look",
    "{topic}: the argument we keep getting wrong",
    "Rethinking {topic}: a case for change",
    "Beyond the debate: rethinking {topic}",
    "The hidden costs of ignoring {topic}",
    "Taking a stand on {topic}",
    "{topic} on trial: weighing the evidence",
  ],
  analytical: [
    "{topic}: a critical analysis",
    "Unpacking {topic}: patterns and meanings",
    "The anatomy of {topic}",
    "Reading between the lines: {topic} examined",
    "{topic} under the microscope",
    "What we learn from {topic}: an analytical perspective",
    "Deconstructing {topic}",
    "The deeper logic of {topic}",
  ],
  expository: [
    "Understanding {topic}: a complete overview",
    "{topic} explained: key ideas and context",
    "The workings of {topic} and why they matter",
    "A closer look at {topic}",
    "The essentials of {topic}",
    "{topic}: origins, development, and impact",
    "An introduction to {topic}",
    "The story behind {topic}",
  ],
  compare: [
    "{topic}: two sides of the same coin",
    "Comparing perspectives on {topic}",
    "{topic} in contrast: similarities and differences",
    "Common ground and key differences in {topic}",
    "Weighing the alternatives: {topic}",
    "{topic} side by side",
    "More alike than different? Examining {topic}",
    "Contrasts and connections in {topic}",
  ],
};

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with",
]);

const HAS_UPPERCASE = /\p{Lu}/u;

function capitalize(word: string): string {
  if (word.length === 0) {
    return word;
  }
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Title case that keeps capitals the writer typed (NASA, AI, iPhone, McDonald's) and
 * capitalises the first word of a subtitle after a colon (APA 7, MLA 9, Chicago 18).
 */
export function toTitleCase(text: string): string {
  const words = text.split(" ");
  return words
    .map((word, index) => {
      if (HAS_UPPERCASE.test(word)) {
        return word;
      }
      const isEdge = index === 0 || index === words.length - 1;
      const afterColon = index > 0 && words[index - 1].endsWith(":");
      if (!(isEdge || afterColon) && SMALL_WORDS.has(word)) {
        return word;
      }
      return capitalize(word);
    })
    .join(" ");
}

const ENGLISH = /^en(?:-|$)/i;

/** Capitalises the first character by the language's own rules (Turkish i becomes İ). */
function capitalizeLocale(text: string, locale: string): string {
  if (text === "") {
    return text;
  }
  const first = String.fromCodePoint(text.codePointAt(0) ?? 0);
  try {
    return first.toLocaleUpperCase(locale) + text.slice(first.length);
  } catch {
    return first.toUpperCase() + text.slice(first.length);
  }
}

/**
 * Fills the title frames with the writer's topic. English titles get title case, which
 * keeps the capitals the writer typed; other languages write sentence-case titles, so
 * the frame is used as translated and only its first letter is capitalised.
 */
export function titlesFor(
  topic: string,
  essayType: EssayType,
  frames: readonly string[] = TITLE_FRAMES[essayType],
  locale = "en"
): string[] {
  return frames.map((frame) => {
    const title = t(frame, { topic });
    return ENGLISH.test(locale)
      ? toTitleCase(title)
      : capitalizeLocale(title, locale);
  });
}
